/**
 * Runs async work for the latest event only: if a new event arrives while a run
 * is in flight, keep the newest pending and start another run after the current
 * one finishes (older mid-flight events are dropped).
 */
export class LatestEventRefreshGate<TEvent> {
  private refreshing = false;
  private pending: TEvent | null = null;

  request(event: TEvent, run: (event: TEvent) => Promise<void>): void {
    if (this.refreshing) {
      this.pending = event;
      return;
    }

    this.refreshing = true;
    void this.runLoop(event, run);
  }

  /** Test helpers */
  get isRefreshing(): boolean {
    return this.refreshing;
  }

  get pendingEvent(): TEvent | null {
    return this.pending;
  }

  private async runLoop(
    event: TEvent,
    run: (event: TEvent) => Promise<void>
  ): Promise<void> {
    try {
      await run(event);
    } catch {
      // Caller is responsible for logging; keep draining queued events.
    } finally {
      const next = this.pending;
      this.pending = null;
      if (next !== null) {
        await this.runLoop(next, run);
      } else {
        this.refreshing = false;
      }
    }
  }
}
